import { useCallback, useEffect, useState } from "react"
import type { OtkDestructionAct } from "shared"

import { getDestructionActById } from "@/lib/api/destructionActs"

type State =
  | { status: "idle" }
  | { status: "ready"; actId: string; act: OtkDestructionAct }
  | { status: "error"; actId: string; error: string }

/**
 * Backs `ActViewDialog`'s on-demand open — warehouse.md's "Акт Знищення"
 * column and warehouse-acts.md's archive both resolve an act by id against
 * the real `GET /otk/destruction-acts/:id`, not from a locally cached list
 * (the act's own `items` already embed series/number, so there is nothing
 * else to resolve against a live blanks list either). State is keyed by the
 * `actId` it was resolved for rather than reset synchronously at the top of
 * the effect, so a stale `ready`/`error` from a previous id reads as
 * "loading" until its own fetch settles, without ever calling a setter
 * outside a promise callback. `replaceAct` swaps in the act a manual retry
 * returned, so the dialog doesn't refetch what the retry already answered.
 */
export function useDestructionAct(actId: string | null) {
  const [state, setState] = useState<State>({ status: "idle" })

  useEffect(() => {
    if (!actId) return
    let cancelled = false
    getDestructionActById(actId)
      .then((act) => {
        if (!cancelled) setState({ status: "ready", actId, act })
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            status: "error",
            actId,
            error: "Не вдалося завантажити акт знищення.",
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [actId])

  const replaceAct = useCallback((act: OtkDestructionAct) => {
    setState({ status: "ready", actId: act.id, act })
  }, [])

  if (!actId) return { act: null, isLoading: false, error: null, replaceAct }
  if (state.status === "ready" && state.actId === actId) {
    return { act: state.act, isLoading: false, error: null, replaceAct }
  }
  if (state.status === "error" && state.actId === actId) {
    return { act: null, isLoading: false, error: state.error, replaceAct }
  }
  return { act: null, isLoading: true, error: null, replaceAct }
}
